import { useId, useMemo, useState } from "react";

export interface TrendPoint {
  date: string;
  value: number;
}

const dateShort = new Intl.DateTimeFormat("it-IT", { day: "2-digit", month: "short" });
const dateLong = new Intl.DateTimeFormat("it-IT", { weekday: "short", day: "numeric", month: "long" });

function day(value: string, format = dateShort) {
  return format.format(new Date(`${value}T12:00:00Z`));
}

function niceMax(value: number) {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const step = [1, 2, 2.5, 5, 10].find((item) => item * magnitude >= value) ?? 10;
  return step * magnitude;
}

export default function TrendChart({
  points,
  color,
  format,
  invert = false,
  compact = false,
  label,
}: {
  points: TrendPoint[];
  color: string;
  format: (value: number) => string;
  invert?: boolean;
  compact?: boolean;
  label: string;
}) {
  const gradientId = useId();
  const [active, setActive] = useState<number | null>(null);
  const scale = useMemo(() => {
    const values = points.map((point) => point.value);
    if (invert) {
      const best = Math.max(1, Math.floor(Math.min(...values)));
      const worst = Math.ceil(Math.max(...values));
      return { lo: best, hi: Math.max(best + 1, worst) };
    }
    return { lo: 0, hi: niceMax(Math.max(...values, 0)) };
  }, [invert, points]);

  if (!points.length) return null;

  const span = scale.hi - scale.lo || 1;
  const yOf = (value: number) =>
    invert ? (value - scale.lo) / span : 1 - (value - scale.lo) / span;
  const xOf = (index: number) => (points.length === 1 ? 0.5 : index / (points.length - 1));
  const line = points
    .map((point, index) => `${index ? "L" : "M"}${(xOf(index) * 1000).toFixed(1)},${(yOf(point.value) * 100).toFixed(2)}`)
    .join(" ");
  const area = invert ? "" : `${line} L1000,100 L0,100 Z`;
  const ticks = [0, 1 / 3, 2 / 3, 1].map((fraction) => {
    const value = invert ? scale.lo + span * fraction : scale.hi - span * fraction;
    return { fraction, value };
  });
  const current = active === null ? null : points[active];
  const total = points.reduce((sum, point) => sum + point.value, 0);
  const peak = points.reduce(
    (best, point) => (invert ? point.value < best.value : point.value > best.value) ? point : best,
    points[0],
  );

  function pick(clientX: number, box: DOMRect) {
    const fraction = Math.min(1, Math.max(0, (clientX - box.left) / box.width));
    setActive(Math.round(fraction * (points.length - 1)));
  }

  return (
    <figure className={`trend-chart${compact ? " compact" : ""}`} style={{ "--trend": color } as React.CSSProperties}>
      <div className="trend-plot">
        <div className="trend-y" aria-hidden="true">
          {ticks.map((tick) => (
            <span key={tick.fraction} style={{ top: `${tick.fraction * 100}%` }}>
              {format(tick.value)}
            </span>
          ))}
        </div>
        <div
          className="trend-canvas"
          role="img"
          aria-label={`${label}: ${points.length} giorni, ${invert ? `migliore ${format(peak.value)}` : `totale ${format(total)}`}`}
          tabIndex={0}
          onPointerMove={(event) => pick(event.clientX, event.currentTarget.getBoundingClientRect())}
          onPointerDown={(event) => pick(event.clientX, event.currentTarget.getBoundingClientRect())}
          onPointerLeave={(event) => event.pointerType === "mouse" && setActive(null)}
          onBlur={() => setActive(null)}
          onKeyDown={(event) => {
            if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
            event.preventDefault();
            const start = active ?? points.length - 1;
            setActive(Math.min(points.length - 1, Math.max(0, start + (event.key === "ArrowRight" ? 1 : -1))));
          }}
        >
          <svg viewBox="0 0 1000 100" preserveAspectRatio="none" aria-hidden="true">
            <defs>
              <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity="0.12" />
                <stop offset="100%" stopColor={color} stopOpacity="0" />
              </linearGradient>
            </defs>
            {ticks.map((tick) => (
              <line key={tick.fraction} x1="0" x2="1000" y1={tick.fraction * 100} y2={tick.fraction * 100} className="trend-grid" vectorEffect="non-scaling-stroke" />
            ))}
            {area && <path d={area} fill={`url(#${gradientId})`} />}
            <path d={line} fill="none" stroke={color} strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
          </svg>
          {current && active !== null && (
            <>
              <i className="trend-cursor" style={{ left: `${xOf(active) * 100}%` }} />
              <b className="trend-dot" style={{ left: `${xOf(active) * 100}%`, top: `${yOf(current.value) * 100}%` }} />
              <div
                className={`trend-tip${xOf(active) > 0.7 ? " left" : ""}`}
                style={{ left: `${xOf(active) * 100}%` }}
              >
                <small>{day(current.date, dateLong)}</small>
                <strong>{format(current.value)}</strong>
              </div>
            </>
          )}
        </div>
      </div>
      <figcaption className="trend-axis">
        <span>{day(points[0].date)}</span>
        {points.length > 2 && <span>{day(points[Math.floor(points.length / 2)].date)}</span>}
        <span>{day(points[points.length - 1].date)}</span>
      </figcaption>
    </figure>
  );
}
